import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import {
  AddIngestorIncomingWebhookBody,
  IncomingIngestorWebhookAttributes,
  IngestedMappedEventResult,
  UpdateIngestorIncomingWebhookBody,
} from './types/incoming-webhook';
import { EarnEventPayload, EventEnveloppe } from '../ingestor-types';

export interface RepublishEventsRequestBody {
  eventIds: string[];
}

export interface RepublishEventsResponse {
  republished: string[];
  // @deprecated use missingEvents instead.
  notRepublished: string[]; // todo rename to missingEvents
  missingEvents: string[];
}

export class IngestorApiClient extends ApiClient {
  static serviceName = 'ingestor-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/ingestor', http, context, options);
  }

  async getProgramIncomingWebhooks(
    programId: string,
    options?: RouteOptions,
  ): Promise<IncomingIngestorWebhookAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/incoming-webhooks`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getAccountIncomingWebhooks(
    accountId: string,
    options?: RouteOptions,
  ): Promise<IncomingIngestorWebhookAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/incoming-webhooks`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getAllIncomingWebhooks(
    options?: RouteOptions,
  ): Promise<IncomingIngestorWebhookAttributes[]> {
    const { data } = await this.http.get(`/all-incoming-webhooks`, defaultAxiosConfig(options));
    return data;
  }

  async addIncomingWebhook(
    programId: string,
    body: AddIngestorIncomingWebhookBody,
    options?: RouteOptions,
  ): Promise<IncomingIngestorWebhookAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/incoming-webhooks`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  /**
   * This call is here because we don't want ingestor API to call gamification api client. Instead
   * we have manager API which handles both
   * @param webhookId
   * @param options
   */
  async getIncomingWebhook(
    webhookId: string,
    options?: RouteOptions,
  ): Promise<IncomingIngestorWebhookAttributes> {
    const { data } = await this.http.get(
      `/incoming-webhooks/${webhookId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateIncomingWebhook(
    webhookId: string,
    body: UpdateIngestorIncomingWebhookBody,
    options?: RouteOptions,
  ): Promise<IncomingIngestorWebhookAttributes> {
    const { data } = await this.http.put(
      `/incoming-webhooks/${webhookId}`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteIncomingWebhook(webhookId: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/incoming-webhooks/${webhookId}`, defaultAxiosConfig(options));
  }

  async deleteProgramIncomingWebhooks(
    programId: string,
    options?: RouteOptions,
  ): Promise<{ affected: number }> {
    const { data } = await this.http.delete(
      `/programs/${programId}/incoming-webhooks`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getEvent<T extends EarnEventPayload>(
    eventId: string,
    options?: RouteOptions,
  ): Promise<EventEnveloppe<T>> {
    const { data } = await this.http.get(`/events/${eventId}`, defaultAxiosConfig(options));
    return data;
  }

  async ingestEvent<T extends EarnEventPayload>(
    event: T | T[],
    options?: RouteOptions,
  ): Promise<EventEnveloppe<T>> {
    const { data } = await this.http.post(`/events`, event, defaultAxiosConfig(options));
    return data;
  }

  async republishEvents(
    eventIds: string[],
    options?: RouteOptions,
  ): Promise<RepublishEventsResponse> {
    const { data } = await this.http.post(
      `/events/republish`,
      { eventIds },
      defaultAxiosConfig(options),
    );

    return data;
  }

  async ingestViaInternalIncomingWebhook(
    incomingWebhookId: string,
    tokenKey: string,
    tokenValue: string,
    events: { timestamp: string; [key: string]: any }[],
    options?: RouteOptions,
  ): Promise<IngestedMappedEventResult[]> {
    const { data } = await this.http.post(
      `/mapped-events/${incomingWebhookId}?${tokenKey}=${tokenValue}`,
      events,
      defaultAxiosConfig(options),
    );
    return data;
  }
}
